import { formatINR } from "../AddMoneyToUser/addMoneyUtils";

// What a ledger row means to the admin. A refund puts money back (green) but is
// NOT "added" money: it lowers Total Used. The backend does that arithmetic.
export const TRANSACTION_TYPES = {
  TOPUP_APPROVED: { label: "Top-up approved", tone: "credit" },
  ADMIN_TOPUP: { label: "Added by admin", tone: "credit" },
  POLICY_DEBIT: { label: "Policy debit", tone: "debit" },
  POLICY_DEBIT_REVERSAL: { label: "Refund (debit reversed)", tone: "credit" },
};

export const typeInfo = (type) => TRANSACTION_TYPES[type] || { label: type || "Other", tone: "neutral" };

export const TONE_COLORS = {
  credit: { color: "#027A48", bg: "#ECFDF3", border: "#ABEFC6" },
  debit: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  neutral: { color: "#344054", bg: "#F2F4F7", border: "#D0D5DD" },
};

/** "+ ₹ 1,000.00", "− ₹ 300.00" (a real minus sign), or "₹ 0.00". */
export const formatSigned = (value) => {
  const amount = Number(value) || 0;
  if (amount === 0) return formatINR(0);
  return `${amount < 0 ? "−" : "+"} ${formatINR(Math.abs(amount))}`;
};

// The server returns exact paise-based differences, so anything at or above half
// a paisa is a real difference, never rounding noise.
const REAL_DIFFERENCE = 0.005;

/**
 * Plain-language warnings for a wallet row or statement. Empty when the wallet
 * balance is fully explained by its transaction history and every approved
 * top-up reached it.
 */
export const walletWarnings = (wallet) => {
  const warnings = [];
  const uncredited = Number(wallet?.uncreditedApproved) || 0;
  const difference = Number(wallet?.ledgerDifference) || 0;

  if (uncredited >= REAL_DIFFERENCE) {
    warnings.push(`Approved top-ups worth ${formatINR(uncredited)} are not in this wallet balance.`);
  } else if (uncredited <= -REAL_DIFFERENCE) {
    warnings.push(`This wallet has ${formatINR(-uncredited)} more top-up credit than approved requests.`);
  }
  if (Math.abs(difference) >= REAL_DIFFERENCE) {
    warnings.push(
      `The balance does not match the transaction history: it is ${formatINR(Math.abs(difference))} ${
        difference > 0 ? "higher" : "lower"
      } than the history shows.`,
    );
  }
  return warnings;
};

/** What a user (not the admin) is told when their wallet does not add up: no amounts, just what to do. */
export const userWalletNotice = (wallet) =>
  walletWarnings(wallet).length > 0
    ? "Your balance does not match your transaction history. Please contact your admin."
    : "";

/**
 * A debit or refund whose policy still exists can be opened: the row's `referenceId` is the policy's own _id,
 * which is what the policy page's address takes.
 */
export const canOpenPolicy = (row) => Boolean(row?.policy && !row.policy.deleted && row.referenceId);

/**
 * The details column: up to three lines. For a policy row: who and which policy / the vehicle, company and policy
 * number / the ledger's own remark (for a new debit that is "Motor Premium ₹... + PA Cover ₹... (incl. GST)").
 */
export const describeRow = (row) => {
  const policy = row?.policy;
  if (policy?.deleted) {
    return { primary: "Policy deleted", secondary: row.remarks || "", tertiary: "", muted: true };
  }
  if (policy) {
    const who = [policy.policyId, policy.customerName].filter(Boolean).join(" · ");
    return {
      primary: who || row.remarks || "Policy",
      secondary: [policy.vehicle, policy.company, policy.policyNumber ? `Policy No ${policy.policyNumber}` : ""]
        .filter(Boolean)
        .join(" · "),
      tertiary: who ? row.remarks || "" : "",
    };
  }
  return {
    primary: row?.remarks || "", // empty: the type chip already says what this row is
    secondary: row?.createdByName ? `By ${row.createdByName}` : "",
    tertiary: "",
  };
};
